import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  ArrowRight,
  ChartBar,
  Code,
  Cube,
  PaperPlaneTilt,
  Plus,
  SquaresFour,
} from '@phosphor-icons/react';

const CARDS = [
  {
    id: 'my-apps',
    icon: SquaresFour,
    color: '#6366F1',
    title: 'My Apps',
    description: 'View and manage apps you\'ve published to the marketplace.',
    cta: 'View My Apps',
    route: '/app-store?filter=mine',
  },
  {
    id: 'submissions',
    icon: PaperPlaneTilt,
    color: '#EC4899',
    title: 'Submissions',
    description: 'Track the status of your app submissions and reviews.',
    cta: 'View Submissions',
    route: '/my-submissions',
  },
  {
    id: 'analytics',
    icon: ChartBar,
    color: '#14B8A6',
    title: 'App Analytics',
    description: 'See how your published apps are performing across the platform.',
    cta: 'View Analytics',
    route: '/analytics',
  },
  {
    id: 'submit',
    icon: Plus,
    color: '#F59E0B',
    title: 'Submit New App',
    description: 'Package and submit a new app or integration to the marketplace.',
    cta: 'Submit App',
    route: '/app-store',
  },
];

export default function DeveloperPortal() {
  const navigate = useNavigate();

  return (
    <>
      <SEO title="Developer Portal | Man & Machine" />
      <div className="min-h-screen p-4 md:p-6 lg:p-8 space-y-8" data-testid="developer-portal">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[rgba(99,102,241,0.12)] flex items-center justify-center">
              <Code size={22} weight="duotone" className="text-[#6366F1]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="developer-portal-title">
                Developer Portal
              </h1>
              <p className="text-sm text-[#8b8fa3]">Build, publish, and manage apps and integrations for your AI agents</p>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="developer-portal-cards">
          {CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.id}
                onClick={() => navigate(card.route)}
                className="group text-left rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-5 hover:border-[rgba(255,255,255,0.15)] hover:bg-[rgba(255,255,255,0.04)] transition-all duration-200"
                data-testid={`developer-card-${card.id}`}
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${card.color}15` }}
                >
                  <Icon size={20} weight="duotone" style={{ color: card.color }} />
                </div>
                <h3 className="text-white text-sm font-semibold mb-1.5">{card.title}</h3>
                <p className="text-[#555] text-xs leading-relaxed mb-4">{card.description}</p>
                <span className="text-xs font-medium flex items-center gap-1.5 transition-colors" style={{ color: card.color }}>
                  {card.cta}
                  <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
                </span>
              </button>
            );
          })}
        </div>

        {/* Quick Links */}
        <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-5">
          <h2 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Cube size={14} className="text-[#6366F1]" />
            Quick Links
          </h2>
          <div className="grid sm:grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/app-store')}
              className="text-left text-xs text-[#8b8fa3] hover:text-white px-3 py-2 rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-all flex items-center gap-2"
              data-testid="developer-quick-appstore"
            >
              <Cube size={14} /> Browse App Store
            </button>
            <button
              onClick={() => navigate('/automations')}
              className="text-left text-xs text-[#8b8fa3] hover:text-white px-3 py-2 rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-all flex items-center gap-2"
              data-testid="developer-quick-automations"
            >
              <SquaresFour size={14} /> Browse Automations
            </button>
            <button
              onClick={() => navigate('/settings/automation-integrations')}
              className="text-left text-xs text-[#8b8fa3] hover:text-white px-3 py-2 rounded-lg hover:bg-[rgba(255,255,255,0.04)] transition-all flex items-center gap-2"
              data-testid="developer-quick-integrations"
            >
              <Code size={14} /> Manage Integrations
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
